// Bidirectional text (Hebrew, Arabic and other right-to-left scripts mixed with left-to-right
// text and numbers), after the Unicode Bidirectional Algorithm (UAX #9) for implicit levels:
// paragraph direction (P2–P3), weak types (W1–W7), neutrals (N1–N2), levels (I1–I2), trailing
// whitespace (L1) and reordering (L2). Explicit embedding controls (LRE, RLO, isolates…) are
// treated as neutral.

type BidiClass = 'L' | 'R' | 'AL' | 'EN' | 'AN' | 'ES' | 'ET' | 'CS' | 'NSM' | 'WS' | 'ON';

/** Any character that makes text right-to-left (fast check before running the algorithm). */
export const RTL_CHARS = /[֐-ࣿיִ-﷿ﹰ-ﻼ]|[\u{10800}-\u{10fff}]|[\u{1e800}-\u{1efff}]/u;

function classOf(cp: number, ch: string): BidiClass {
  if (cp >= 0x30 && cp <= 0x39) return 'EN';
  if (cp === 0x2b || cp === 0x2d || cp === 0x2212) return 'ES';
  if (cp === 0x2c || cp === 0x2e || cp === 0x3a || cp === 0x2f || cp === 0xa0 || cp === 0x60c) return 'CS';
  if (cp === 0x23 || cp === 0x24 || cp === 0x25 || cp === 0xb0 || cp === 0xb1 || (cp >= 0xa2 && cp <= 0xa5) || cp === 0x2030 || cp === 0x2031 || (cp >= 0x20a0 && cp <= 0x20cf) || cp === 0x66a) return 'ET';
  if (cp === 0x20 || cp === 0x09 || cp === 0x0c || (cp >= 0x2000 && cp <= 0x200a) || cp === 0x2028 || cp === 0x205f || cp === 0x3000) return 'WS';
  if (cp >= 0x6f0 && cp <= 0x6f9) return 'EN';
  if ((cp >= 0x660 && cp <= 0x669) || cp === 0x66b || cp === 0x66c) return 'AN';
  if (/\p{Mn}|\p{Me}/u.test(ch)) return 'NSM';
  if ((cp >= 0x590 && cp <= 0x5ff) || (cp >= 0x7c0 && cp <= 0x85f) || (cp >= 0xfb1d && cp <= 0xfb4f) || (cp >= 0x10800 && cp <= 0x10fff) || (cp >= 0x1e800 && cp <= 0x1efff)) return 'R';
  if ((cp >= 0x600 && cp <= 0x7bf) || (cp >= 0x860 && cp <= 0x8ff) || (cp >= 0xfb50 && cp <= 0xfdff) || (cp >= 0xfe70 && cp <= 0xfefc)) return 'AL';
  if (/[\p{L}\p{Nd}\p{Mc}]/u.test(ch)) return 'L';
  return 'ON';
}

const strong = (t: BidiClass) => t === 'L' || t === 'R' || t === 'AL';

/** Direction of a paragraph from its first strong character (left-to-right when there is none). */
export function paragraphLevel(text: string): 0 | 1 {
  for (const ch of text) {
    const t = classOf(ch.codePointAt(0)!, ch);
    if (t === 'L') return 0;
    if (t === 'R' || t === 'AL') return 1;
  }
  return 0;
}

/** Embedding level of every UTF-16 unit of a paragraph (even: left-to-right, odd: right-to-left). */
export function bidiLevels(text: string, base: 0 | 1 = paragraphLevel(text)): Uint8Array {
  // classes per code point, mapped back to UTF-16 units at the end
  const cps: { cls: BidiClass; at: number; len: number }[] = [];
  for (let i = 0; i < text.length; ) {
    const cp = text.codePointAt(i)!;
    const len = cp > 0xffff ? 2 : 1;
    cps.push({ cls: classOf(cp, text.slice(i, i + len)), at: i, len });
    i += len;
  }
  const t = cps.map((c) => c.cls);
  const sor: BidiClass = base ? 'R' : 'L';
  // W1: marks take the type of what they follow
  for (let i = 0; i < t.length; i++) if (t[i] === 'NSM') t[i] = i ? (t[i - 1] === 'NSM' ? sor : t[i - 1]) : sor;
  // W2: European digits after Arabic letters are Arabic numbers; W3: AL → R
  let last: BidiClass = sor;
  for (let i = 0; i < t.length; i++) {
    if (strong(t[i])) last = t[i];
    else if (t[i] === 'EN' && last === 'AL') t[i] = 'AN';
  }
  for (let i = 0; i < t.length; i++) if (t[i] === 'AL') t[i] = 'R';
  // W4: one separator between two numbers of the same kind joins them
  for (let i = 1; i < t.length - 1; i++) {
    if (t[i] === 'ES' && t[i - 1] === 'EN' && t[i + 1] === 'EN') t[i] = 'EN';
    else if (t[i] === 'CS' && t[i - 1] === t[i + 1] && (t[i - 1] === 'EN' || t[i - 1] === 'AN')) t[i] = t[i - 1];
  }
  // W5: terminators (currency, %) next to European numbers become numbers
  for (let i = 0; i < t.length; i++) {
    if (t[i] !== 'ET') continue;
    let j = i;
    while (j < t.length && t[j] === 'ET') j++;
    if ((i > 0 && t[i - 1] === 'EN') || (j < t.length && t[j] === 'EN')) for (let k = i; k < j; k++) t[k] = 'EN';
    i = j - 1;
  }
  // W6: leftover separators and terminators are neutral
  for (let i = 0; i < t.length; i++) if (t[i] === 'ES' || t[i] === 'ET' || t[i] === 'CS') t[i] = 'ON';
  // W7: European numbers in left-to-right context are L
  last = sor;
  for (let i = 0; i < t.length; i++) {
    if (t[i] === 'L' || t[i] === 'R') last = t[i];
    else if (t[i] === 'EN' && last === 'L') t[i] = 'L';
  }
  // N1/N2: neutrals between the same direction take it, otherwise the paragraph's
  const dirOf = (c: BidiClass) => (c === 'L' ? 'L' : c === 'R' || c === 'EN' || c === 'AN' ? 'R' : null);
  for (let i = 0; i < t.length; i++) {
    if (dirOf(t[i])) continue;
    let j = i;
    while (j < t.length && !dirOf(t[j])) j++;
    const before = i > 0 ? dirOf(t[i - 1]) : sor;
    const after = j < t.length ? dirOf(t[j]) : sor;
    const d = before === after ? before! : base ? 'R' : 'L';
    for (let k = i; k < j; k++) t[k] = d as BidiClass;
    i = j - 1;
  }
  // I1/I2: levels
  const out = new Uint8Array(text.length);
  cps.forEach((c, i) => {
    const cls = t[i];
    const lv = base === 0 ? (cls === 'R' ? 1 : cls === 'AN' || cls === 'EN' ? 2 : 0) : cls === 'L' || cls === 'EN' || cls === 'AN' ? 2 : 1;
    for (let k = 0; k < c.len; k++) out[c.at + k] = lv;
  });
  return out;
}

export interface BidiRun {
  /** UTF-16 range within the line */
  start: number;
  end: number;
  level: number;
}

/**
 * Runs of one line in visual (left-to-right display) order. `levels` covers the line (a slice of
 * the paragraph's levels); trailing whitespace takes the paragraph level (L1).
 */
export function visualRuns(line: string, levels: Uint8Array, base: 0 | 1): BidiRun[] {
  const lv = Uint8Array.from(levels.subarray(0, line.length));
  for (let i = line.length - 1; i >= 0 && /\s/.test(line[i]); i--) lv[i] = base;
  const runs: BidiRun[] = [];
  for (let i = 0; i < line.length; ) {
    let j = i + 1;
    while (j < line.length && lv[j] === lv[i]) j++;
    runs.push({ start: i, end: j, level: lv[i] });
    i = j;
  }
  // L2: from the highest level down to the lowest odd one, reverse every sequence at or above it
  const max = Math.max(0, ...runs.map((r) => r.level));
  const minOdd = Math.min(...runs.map((r) => (r.level % 2 ? r.level : 99)), 99);
  for (let level = max; level >= minOdd && level > 0; level--) {
    for (let i = 0; i < runs.length; ) {
      if (runs[i].level < level) {
        i++;
        continue;
      }
      let j = i;
      while (j < runs.length && runs[j].level >= level) j++;
      const seq = runs.slice(i, j).reverse();
      runs.splice(i, j - i, ...seq);
      i = j;
    }
  }
  return runs;
}
